import { PropsWithChildren, useContext, useMemo, useState } from "react";
import { Button, Spinner, ToolbarButton, ToolbarDivider, ToolbarGroup, makeStyles, shorthands, tokens } from "@fluentui/react-components";
import {
    InfoRegular
} from "@fluentui/react-icons";
import { IFormButtonsProps } from "./form-buttons.props";
import { useL10n } from "context/l10n-context";
import { ILocaleStrings } from "loc/locale.interfaces";
import { IApiError } from "@interfaces/common.interfaces";
import { getError } from "lib/utils";
import ApiErrorBar from "components/common/api-error-bar/api-error-bar";
import { FieldValues, useFormContext } from "react-hook-form";

const layoutStyles = makeStyles({
    mainWrapper: {
        ...shorthands.margin('1.5em', 0, '0.5em', 0),
        display: 'flex'
    },
    errorLabel: {
        color: tokens.colorStatusDangerForeground1
    },
    left: {
        flexGrow: 1,
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center'
    },
    right: {
        display: 'flex',
        justifyContent: 'flex-end',
        '> button': {
            ...shorthands.margin(0, 0, 0, '1em')
        }
    }
});

export default function FormButtons<T extends FieldValues>(props: IFormButtonsProps<T>) {
    const styles = layoutStyles();
    const { t } = useL10n<ILocaleStrings>();
    const { formState: { errors, isSubmitted, isSubmitting, isLoading } } = useFormContext<T>();

    const onCancelHandler = () => {
        if (props.onCancel)
            props.onCancel();
    }

    const hasError = () => {
        return props.apiError || Object.keys(errors).length > 0;
    }

    if (isLoading)
        return (<></>);

    return (
        <div className={styles.mainWrapper}>
            <div className={styles.left}>
                {isSubmitting &&
                    <Spinner label={props.submittingLabel ? t(props.submittingLabel) : t('submitting')} size="tiny" />}
                {hasError() &&
                    <span className={styles.errorLabel}>{t('error')}</span>}
            </div>
            <div className={styles.right}>
                <Button
                    type="submit"
                    appearance="primary"
                    disabled={isSubmitting}>{t(props.submitLabel)}</Button>
                {props.onCancel &&
                    <Button
                        appearance="secondary"
                        onClick={onCancelHandler}
                        disabled={isSubmitting}>{props.cancelLabel ? t(props.cancelLabel) : t("cancel")}</Button>}
            </div>
        </div>
    );
}